import React from 'react';
import { useTranslation } from 'react-i18next';
import { Avatar, Box, Tooltip, Typography } from '@mui/material';
import { Person as PersonIcon } from '@mui/icons-material';
import { UserIdentity } from '@/types/user';
import { UserAvatar } from '../../../ui/UserAvatar';
import { getDocumentAccess } from './documentAccess';
import type { OwnerCellProps } from '../types';

/**
 * OwnerCell - Display document owner with avatar
 *
 * Pure presentational component that shows the document owner
 * using UserIdentity and UserAvatar. Falls back to a generic
 * person icon if owner information is not available.
 *
 * @param doc - The document to display owner for
 */
export const OwnerCell: React.FC<OwnerCellProps> = ({ doc, currentUserId }) => {
  const { t } = useTranslation();
  const identity = UserIdentity.fromDocument(doc as any);
  const { isOwner } = getDocumentAccess(doc, currentUserId);

  // Check if we have any owner information
  if (identity.isEmpty()) {
    return (
      <Tooltip title={t('documents.table.noOwner')}>
        <Avatar sx={{ width: 24, height: 24, fontSize: '0.75rem' }}>
          <PersonIcon fontSize="small" />
        </Avatar>
      </Tooltip>
    );
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <UserAvatar
        user={doc as any}
        size="small"
        customSize={24}
        tooltipText={`${t('documents.table.owner')}: ${identity.getTooltipText()}${isOwner ? ` (${t('documents.table.you')})` : ''}`}
      />
      <Typography
        variant="caption"
        sx={{
          maxWidth: 100,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        }}
      >
        {isOwner ? t('documents.table.you') : identity.getDisplayName()}
      </Typography>
    </Box>
  );
};

OwnerCell.displayName = 'OwnerCell';
